const TASA_IVA_MX = 0.16;
const TASA_IVA_CO = 0.19;

function redondear(n: number): number {
  return Math.round(n * 100) / 100;
}

export interface TaxableItem {
  precio: number;
  cantidad: number;
  esEnvio: boolean;
}

export interface ResumenImpuestosMX {
  baseGravableEnvios: number;
  ivaEnvios: number;
  baseGravableEmpaques: number;
  ivaEmpaques: number;
  baseGravable: number;
  totalImpuestos: number;
  total: number;
}

export function extraerIVA(
  precioConIVA: number,
  tasa = TASA_IVA_MX
): { base: number; iva: number } {
  const base = redondear(precioConIVA / (1 + tasa));
  const iva = redondear(precioConIVA - base);
  return { base, iva };
}

export function calcularIVA(
  precioBase: number,
  tasa = TASA_IVA_MX
): { iva: number; total: number } {
  const iva = redondear(precioBase * tasa);
  return { iva, total: redondear(precioBase + iva) };
}

export function calcularResumenImpuestosMX(items: TaxableItem[]): ResumenImpuestosMX {
  let baseGravableEnvios = 0;
  let ivaEnvios = 0;
  let baseGravableEmpaques = 0;
  let ivaEmpaques = 0;

  for (const item of items) {
    const precioTotal = item.precio * item.cantidad;
    if (item.esEnvio) {
      // Envío price is IVA-inclusive (valorEnvio from cotización already has IVA)
      const { base, iva } = extraerIVA(precioTotal);
      baseGravableEnvios = redondear(baseGravableEnvios + base);
      ivaEnvios = redondear(ivaEnvios + iva);
    } else {
      // Empaque price is base (catalog price, IVA must be added)
      const { iva } = calcularIVA(precioTotal);
      baseGravableEmpaques = redondear(baseGravableEmpaques + precioTotal);
      ivaEmpaques = redondear(ivaEmpaques + iva);
    }
  }

  const baseGravable = redondear(baseGravableEnvios + baseGravableEmpaques);
  const totalImpuestos = redondear(ivaEnvios + ivaEmpaques);
  const total = redondear(baseGravable + totalImpuestos);

  return {
    baseGravableEnvios,
    ivaEnvios,
    baseGravableEmpaques,
    ivaEmpaques,
    baseGravable,
    totalImpuestos,
    total,
  };
}

export interface TiqueteItemImpuesto {
  subtotal: number;
  tipoLinea: string;
}

/**
 * Recalculates the MX tax breakdown from tiquete items.
 * Tiquete prices are BASE prices (IVA not included), so calcularIVA is
 * used for all item types rather than extraerIVA.
 */
export function calcularResumenMXDesdeTiquete(items: TiqueteItemImpuesto[]): ResumenImpuestosMX {
  let baseGravableEnvios = 0;
  let ivaEnvios = 0;
  let baseGravableEmpaques = 0;
  let ivaEmpaques = 0;

  for (const item of items) {
    const { iva } = calcularIVA(item.subtotal);
    if (item.tipoLinea === 'envio') {
      baseGravableEnvios = redondear(baseGravableEnvios + item.subtotal);
      ivaEnvios = redondear(ivaEnvios + iva);
    } else {
      baseGravableEmpaques = redondear(baseGravableEmpaques + item.subtotal);
      ivaEmpaques = redondear(ivaEmpaques + iva);
    }
  }

  const baseGravable = redondear(baseGravableEnvios + baseGravableEmpaques);
  const totalImpuestos = redondear(ivaEnvios + ivaEmpaques);
  const total = redondear(baseGravable + totalImpuestos);

  return { baseGravableEnvios, ivaEnvios, baseGravableEmpaques, ivaEmpaques, baseGravable, totalImpuestos, total };
}

export function calcularTotalesCO(items: TaxableItem[]): {
  subtotal: number;
  impuestos: number;
  total: number;
} {
  const subtotal = redondear(
    items.reduce((s, i) => s + i.precio * i.cantidad, 0)
  );
  const baseEmpaque = redondear(
    items.reduce((s, i) => (i.esEnvio ? s : s + i.precio * i.cantidad), 0)
  );
  const impuestos = redondear(baseEmpaque * TASA_IVA_CO);
  return { subtotal, impuestos, total: redondear(subtotal + impuestos) };
}
